// material ui
import { Button, Grid, MenuItem, Select } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import SaveIcon from '@material-ui/icons/Save';
import { cloneDeep } from 'lodash-es';
import { MTableToolbar } from 'material-table';
import React, { ReactElement } from 'react';

// apis
import {
  createAPSPriceMapPair,
  deleteAPSPriceMapPair,
  getAPSPriceMapPairs,
  IAPSPriceMapPair,
  updateAPSPriceMapPair,
} from '~/src/dash/api/aps-price-map-pair';
import {
  getAllPrebidPublishers,
  getResellers,
  IPublisher,
  IReseller,
  uploadAPSCSVfile,
  uploadDOOHCSVfile,
  uploadEPGChannelCSVfile,
  uploadGlobalAppBundleBlockListfile,
} from '~/src/dash/api/console-api';
import {
  createDemandSourceRateLimit,
  deleteDemandSourceRateLimit,
  getAllDemandSourceRateLimitsForPublisher,
  IDemandSourceRateLimit,
  updateDemandSourceRateLimit,
} from '~/src/dash/api/demand_source_rate_limit';
import {
  getDOOHVenueMultiplierMapPairs,
  IDOOHVenueMultiplierMapPair,
} from '~/src/dash/api/dooh-venue-multiplier-map-pair';
import { getEPGChannelMapPairs, IEPGChannelMapPair } from '~/src/dash/api/epg-channel-map-pair';
import {
  createGlobalAppBundleBlockList,
  deleteGlobalAppBundleBlockListEntry,
  getGlobalAppBundleBlockList,
  IGlobalAppBundleBlockList,
  updateGlobalAppBundleBlockList,
} from '~/src/dash/api/global-app-bundle-blocklist-api';
import {
  createLiverampCustomIDMapPair,
  deleteLiverampCustomIDMapPair,
  getLiverampCustomIDMapPairs,
  ILiverampCustomIDMapPair,
  updateLiverampCustomIDMapPair,
} from '~/src/dash/api/liveramp-custom-id-map-pair';
import { EBool } from '~/src/dash/api/types/bool';
import {
  IAPSPriceMapPairRow,
  IDemandSourceRateLimitMapPairRow,
  IDOOHVenueMultiplierMapPairRow,
  IEPGChannelMapPairRow,
  IGlobalAppBundleBlockListRowData,
  IListTableColumn,
  ILiverampCustomIDMapPairRow,
  ListTable,
} from '~/src/dash/components/list-table';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
// components
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
import { Panel, Panels } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  genDownloadAPSCSVURL,
  genDownloadDOOHCSVURL,
  genDownloadEPGChannelCSVURL,
  genDownloadGlobalAppBundleBlockListURL,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
// views
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  loading: boolean;
  updating: boolean;
  publishers: IPublisher[];
  resellers: IReseller[];
  APSPriceMapPairs: IAPSPriceMapPair[];
  priceMapData: IAPSPriceMapPairRow[];
  demandSourceRateLimitData: IDemandSourceRateLimitMapPairRow[];
  demandSourceList: { [key: string]: string };
  // demandSourceDropdownList elements will be like <MenuItem value="acuity">Acuity</MenuItem> and will have props like { value: "acuity", children: "Acuity" }
  demandSourceDropdownList: ReactElement[];
  DOOHVenueMultiplierMapPairs: IDOOHVenueMultiplierMapPair[];
  doohVenueMultiplierMapData: IDOOHVenueMultiplierMapPairRow[];
  EPGChannelMapPairs: IEPGChannelMapPair[];
  epgChannelMapData: IEPGChannelMapPairRow[];
  liverampCustomIDMapData: ILiverampCustomIDMapPairRow[];
  globalAppBundleBlockList: IGlobalAppBundleBlockList[];
  globalAppBundleBlockListRowData: IGlobalAppBundleBlockListRowData[];
  enableAppBundleBlockListUpdate: boolean;
  editableKey: string;
  editablePrice: number;
  editablePublisherID: number;
  editableLiverampCustomID: number;
  editableID: string;
  snackbarMessage: string;
  snackbarError: boolean;
}

export const ConfigurationsSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // NOTE: Use styles when the CSS is used more than once
    styles: () => ({
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
      menuButtonTextBlue: {
        marginBottom: 2,
        paddingRight: 8,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#4952FF',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      loading: false,
      updating: false,

      publishers: [] as IPublisher[],
      resellers: [] as IReseller[],

      APSPriceMapPairs: [] as IAPSPriceMapPair[],
      priceMapData: [] as IAPSPriceMapPairRow[],

      demandSourceRateLimitData: [] as IDemandSourceRateLimitMapPairRow[],
      demandSourceList: {} as { [key: string]: string },
      demandSourceDropdownList: [] as ReactElement[],

      DOOHVenueMultiplierMapPairs: [] as IDOOHVenueMultiplierMapPair[],
      doohVenueMultiplierMapData: [] as IDOOHVenueMultiplierMapPairRow[],

      EPGChannelMapPairs: [] as IEPGChannelMapPair[],
      epgChannelMapData: [] as IEPGChannelMapPairRow[],

      LiverampCustomIDMapPairs: [] as ILiverampCustomIDMapPair[],
      liverampCustomIDMapData: [] as ILiverampCustomIDMapPairRow[],

      globalAppBundleBlockList: [] as IGlobalAppBundleBlockList[],
      globalAppBundleBlockListRowData: [] as IGlobalAppBundleBlockListRowData[],
      enableAppBundleBlockListUpdate: false,

      editablePrice: 0,
      editablePublisherID: 0,
      editableLiverampCustomID: 0,
      editableKey: '',
      editableID: '',
      snackbarMessage: '',
      snackbarError: false,
    };

    componentDidMount() {
      this.getAPSPriceMapPairsAndSetTable();
      this.getDOOHVenueMultiplierMapPairsAndSetTable();
      this.getEPGChannelMapPairsAndSetTable();
      this.getLiverampCustomIDMapPairsAndSetTable();
      this.getDemandSourceRateLimitsAndSetTable();
      if (this.props.session.publisher.ID === '1') {
        // only pub 1 can access
        this.getAllPublishersResellersAndAppBundleBlockList();
      }
    }

    generateAPSPriceListTableData = () => {
      const priceMapData: IAPSPriceMapPairRow[] = this.state.APSPriceMapPairs.map(
        (pair: IAPSPriceMapPair) => {
          return {
            ID: pair.ID.toString(),
            Name: pair.PriceKey,
            PriceKey: pair.PriceKey,
            PriceValue: pair.PriceValue,
            EnableEditable: false,
          };
        },
      );
      this.setState({
        priceMapData,
      });
    };

    // |------- API FUNCTIONS --------|
    getAPSPriceMapPairsAndSetTable = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          getAPSPriceMapPairs(this.props.session.publisher.ID)
            .then((APSPriceMapPairs: IAPSPriceMapPair[]) => {
              this.setState(
                {
                  loading: false,
                  APSPriceMapPairs,
                },
                this.generateAPSPriceListTableData,
              );
            })
            .catch(() => {
              this.setState({
                loading: false,
                snackbarError: true,
                snackbarMessage: 'Error loading APS Price Map',
              });
            });
        },
      );
    };

    getDemandSourceRateLimitsAndSetTable = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          // get demand source list for publisher
          const headerBidderAdaptersForPublisher = getHeaderBidderAdaptersForPublisher(
            '',
            false,
            this.props.session.publisher,
            hasRoleSuperUser(this.props.user.user),
          );
          const demandSourceDropdownList: ReactElement[] = [];
          const demandSourceList: { [key: string]: string } = {};

          for (let i = 0; i < headerBidderAdaptersForPublisher.length; i++) {
            demandSourceDropdownList.push(
              <MenuItem value={headerBidderAdaptersForPublisher[i].key}>
                {headerBidderAdaptersForPublisher[i].text}
              </MenuItem>,
            );
            demandSourceList[headerBidderAdaptersForPublisher[i].key] =
              headerBidderAdaptersForPublisher[i].text;
          }

          // get existing demand source rate limit items from publisher
          getAllDemandSourceRateLimitsForPublisher(this.props.session.publisher.ID).then(
            ([demandSourceRateLimitList, err]) => {
              if (err || !demandSourceRateLimitList) {
                this.setState({
                  loading: false,
                  snackbarError: true,
                  snackbarMessage: 'Error Loading Demand Source Rate Limits',
                });
              } else {
                // remove demand sources which already have a set rate limit from demand source dropdown list
                // so that we are not able to add a duplicate item
                const demandSourcesToRemove: string[] = [];
                for (let i = 0; i < demandSourceRateLimitList.length; i++) {
                  demandSourcesToRemove.push(demandSourceRateLimitList[i].DemandSourceName);
                }
                for (let i = 0; i < demandSourceDropdownList.length; i++) {
                  if (demandSourcesToRemove.includes(demandSourceDropdownList[i].props.value)) {
                    demandSourceDropdownList.splice(i, 1);
                  }
                }

                // convert demand source rate limit list to map pairs
                const demandSourceRateLimitMapPairs = demandSourceRateLimitList.map(
                  (demandSourceRateLimitItem: IDemandSourceRateLimit) => {
                    return {
                      ID: demandSourceRateLimitItem.ID.toString(),
                      Name: demandSourceRateLimitItem.DemandSourceName,
                      DemandSource: demandSourceList[demandSourceRateLimitItem.DemandSourceName],
                      RateLimit: demandSourceRateLimitItem.RateLimit,
                    } as IDemandSourceRateLimitMapPairRow;
                  },
                );

                this.setState({
                  loading: false,
                  demandSourceRateLimitData: demandSourceRateLimitMapPairs,
                  demandSourceDropdownList,
                  demandSourceList,
                });
              }
            },
          );
        },
      );
    };

    // get all publishers, resellers, and the App Bundle Block List
    getAllPublishersResellersAndAppBundleBlockList = (): void => {
      this.setState(
        {
          loading: true,
        },
        () => {
          if (hasRoleSuperUser(this.props.user.user)) {
            getAllPrebidPublishers()
              .then((publishers: IPublisher[]) => {
                this.setState(
                  {
                    publishers,
                  },
                  () => {
                    getResellers()
                      .then((resellers: IReseller[]) => {
                        // filter out reseller Publica(1), Roku(4), IAS(8)
                        resellers = resellers.filter(
                          (reseller) => !['1', '4', '8'].includes(reseller.ID),
                        );
                        this.setState(
                          {
                            resellers,
                          },
                          this.getGlobalBlockListDataAndSetTable,
                        );
                      })
                      .catch(() => {
                        this.setState({
                          snackbarMessage: 'Error loading resellers',
                          snackbarError: true,
                          resellers: [] as IReseller[],
                          loading: false,
                        });
                      });
                  },
                );
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Error loading publishers',
                  snackbarError: true,
                  publishers: [] as IPublisher[],
                  loading: false,
                });
              });
          }
        },
      );
    };

    createAPSPriceMapPair = (PriceKey: string, PriceValue: number) => {
      // check to make sure the new key is unique
      for (const pair of this.state.APSPriceMapPairs) {
        if (PriceKey === pair.PriceKey) {
          this.setState({
            snackbarMessage: 'The key must be unique',
            snackbarError: true,
          });
          return;
        }
      }

      this.setState(
        {
          updating: true,
        },
        () => {
          createAPSPriceMapPair(
            {
              ID: 0,
              PublisherID: parseInt(this.props.session.publisher.ID, 10),
              PriceKey,
              PriceValue,
            },
            this.props.session.publisher.ID,
          )
            .then(() => {
              this.setState(
                {
                  updating: false,
                  snackbarMessage: 'Successfully created APS Price Map key-price',
                },
                this.getAPSPriceMapPairsAndSetTable,
              );
            })
            .catch((resp) => {
              this.setState({
                updating: false,
                snackbarMessage: `Failed to create APS Price Map key-price: ${resp}`,
                snackbarError: true,
              });
            });
        },
      );
    };

    uploadAPSPriceMapPair = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }

        uploadAPSCSVfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          file,
        })
          .then((json: any) => {
            if (json.response && json.response.errors) {
              this.setState({
                snackbarMessage: `CSV Upload failed: ${json.response.errors}`,
                snackbarError: true,
              });
            } else {
              this.setState(
                {
                  snackbarMessage: 'CSV Upload successful',
                },
                this.getAPSPriceMapPairsAndSetTable,
              );
            }
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'CSV Upload failed',
              snackbarError: true,
            });
          });
      }
      // reset the value of the event so the same file name can be uploaded
      event.target.value = '';
    };

    deleteAPSPriceMapPair = (id: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteAPSPriceMapPair(parseInt(id, 10), this.props.session.publisher.ID)
            .then(() => {
              this.setState(
                {
                  snackbarMessage: 'API Price Map key-price deleted successfully.',
                  updating: false,
                },
                this.getAPSPriceMapPairsAndSetTable,
              );
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'API Price Map key-price deletion failed',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return Promise.resolve(undefined);
    };

    deleteGlobalAppBundleEntry = (id: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteGlobalAppBundleBlockListEntry(parseInt(id, 10), this.props.session.publisher.ID)
            .then(() => {
              this.setState(
                {
                  snackbarMessage: 'Global App Bundle BlockList Entry deleted successfully.',
                  updating: false,
                },
                this.getGlobalBlockListDataAndSetTable,
              );
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'Global App Bundle BlockList Entry deletion failed',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return Promise.resolve(undefined);
    };

    updateAPSPriceMapPair = (editableID: string, field: string, changedValue: any) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          const priceMapData = cloneDeep(this.state.priceMapData);
          let unchangedValue;
          for (const datum of priceMapData) {
            if (datum.ID === editableID) {
              unchangedValue = field === 'PriceKey' ? datum.PriceValue : datum.PriceKey;
              break;
            }
          }
          const pair = {
            ID: parseInt(editableID, 10),
            PublisherID: parseInt(this.props.session.publisher.ID, 10),
            PriceKey: field === 'PriceKey' ? changedValue : unchangedValue,
            PriceValue: field === 'PriceValue' ? changedValue : unchangedValue,
          } as IAPSPriceMapPair;
          updateAPSPriceMapPair(pair, this.props.session.publisher.ID)
            .then(() => {
              this.getAPSPriceMapPairsAndSetTable();
              this.setState({
                priceMapData,
                snackbarMessage: 'API Price Map key-price successfully updated.',
                updating: false,
              });
            })
            .catch((resp) => {
              this.setState({
                snackbarMessage: `API Price Map key-price failed to update: ${resp}`,
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
    };

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleChangePair = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    handleChangeNumHelper = (stateKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === '') {
        this.setState({
          [stateKey]: 0,
        });
      } else if (Number.isNaN(parseInt(event.target.value, 10))) {
        return;
      } else {
        this.setState({
          [stateKey]: parseInt(event.target.value, 10) || 0,
        });
      }
    };

    handleChangeEditableAPSPriceMap = (editableID: string, field: string, changedValue: any) => {
      if (this.state.updating) {
        return;
      }
      // validate that the name and url are not blank before updating
      if (field === 'PriceKey' && changedValue === '') {
        this.setState({
          snackbarMessage: 'Key cannot be blank',
          snackbarError: true,
        });
        return;
      } else if (field === 'PriceValue' && +changedValue === 0) {
        this.setState({
          snackbarMessage: 'Price cannot be 0',
          snackbarError: true,
        });
        return;
      } else if (
        field === 'PriceKey' &&
        (changedValue.length < 4 || changedValue.slice(0, 3) !== 'ott')
      ) {
        this.setState({
          snackbarMessage: 'Key must start with "ott"',
          snackbarError: true,
        });
        return;
      }
      this.updateAPSPriceMapPair(editableID, field, changedValue);
    };

    renderAPSPriceMapPairs = (): ReactElement => {
      const { priceMapData, loading, updating, editableID, editablePrice, editableKey } =
        this.state;

      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const columns: IListTableColumn[] = [
        { field: 'PriceKey', title: 'Key', renderType: 'editable', initialEditValue: 'Enter Key' },
        { field: 'PriceValue', title: 'Price ($)', renderType: 'editable', initialEditValue: 1 },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      // loop over data to switch the name of the edited row to the temp name
      const clonedData = cloneDeep(priceMapData);
      for (const datum of clonedData) {
        if (datum.ID === editableID) {
          datum.PriceValue = editablePrice;
          datum.PriceKey = editableKey;
          break;
        }
      }

      return (
        <Grid style={{ marginTop: 20, marginLeft: 20 }}>
          <ListTable
            title=''
            columns={columns}
            data={clonedData}
            isLoading={loading}
            isUpdating={updating}
            renderCustomToolbar={(props: any) =>
              this.renderListToolbarUploadDownloadCSV(
                props,
                genDownloadAPSCSVURL(this.props.session.publisher.ID),
                this.uploadAPSPriceMapPair,
                null,
              )
            }
            handleChangeEditable={this.handleChangeEditableAPSPriceMap}
            handleDeleteItem={this.deleteAPSPriceMapPair}
            disableSnackbar={true}
            handleSetUpdating={(updating: boolean) => this.setState({ updating })}
            pageSize={10}
            actionsColumnIndex={2}
            useCustomAddButton={true}
            enableCenterActionCells={true}
            editable={{
              onRowAdd: (rowData: IAPSPriceMapPairRow) => {
                return new Promise((resolve) => {
                  this.createAPSPriceMapPair(rowData.PriceKey, rowData.PriceValue);
                  resolve(undefined);
                });
              },
            }}
            disableUpdateFocus={true}
          />
        </Grid>
      );
    };

    // FUNCTIONS FOR DEMAND SOURCE RATE LIMIT TABLE

    renderEditableDemandSourceDropdownColumn = (
      props: any,
      demandSourceDropdownList: ReactElement[],
    ) => {
      return (
        <Select
          name=''
          style={{ fontSize: 14, minWidth: 100 }}
          onChange={(e) => {
            props.onChange(e.target.value);
          }}
          value={props.value}
        >
          {demandSourceDropdownList}
        </Select>
      );
    };

    deleteDemandSourceRateLimit = (id: string, itemIndex: number) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteDemandSourceRateLimit(id, this.props.session.publisher.ID).then((res) => {
            const err = Array.isArray(res) ? res[1] : null;
            const clonedDemandSourceRateLimitData = cloneDeep(this.state.demandSourceRateLimitData);
            const rateLimitToBeDeleted = clonedDemandSourceRateLimitData[itemIndex];
            if (err) {
              this.setState({
                updating: false,
                snackbarMessage: `error deleting Rate Limit for ${rateLimitToBeDeleted.DemandSource}`,
                snackbarError: true,
              });
              return;
            }

            const clonedDemandSourceDropdownList = cloneDeep(this.state.demandSourceDropdownList);

            // Add demand source back into dropdown list
            clonedDemandSourceDropdownList.push(
              <MenuItem key={rateLimitToBeDeleted.Name} value={rateLimitToBeDeleted.Name}>
                {rateLimitToBeDeleted.DemandSource}
              </MenuItem>,
            );
            // re-alphabetize
            clonedDemandSourceDropdownList.sort((menuItemA, menuItemB) => {
              if (menuItemA.props.children < menuItemB.props.children) {
                return -1;
              }
              if (menuItemA.props.children > menuItemB.props.children) {
                return 1;
              }
              return 0;
            });

            // remove item from demandSourceRateLimitData and save publisher
            clonedDemandSourceRateLimitData.splice(itemIndex, 1);

            this.setState({
              demandSourceRateLimitData: clonedDemandSourceRateLimitData,
              demandSourceDropdownList: clonedDemandSourceDropdownList,
              snackbarMessage: `Successfully deleted Rate Limit for ${rateLimitToBeDeleted.DemandSource}`,
              snackbarError: false,
              updating: false,
            });
          });
        },
      );
      // the delete modal expects a promise to be returned
      return Promise.resolve(undefined);
    };

    createNewDemandSourceRateLimit = (rowData: IDemandSourceRateLimitMapPairRow) => {
      if (this.state.updating) {
        return;
      }

      // validate fields
      if (!rowData.DemandSource) {
        this.setState({
          snackbarMessage: 'Item not saved. You must select a demand source',
          snackbarError: true,
        });
        return;
      }
      if (!rowData.RateLimit) {
        this.setState({
          snackbarMessage: 'Item not saved. You must define a rate limit',
          snackbarError: true,
        });
        return;
      }
      // @ts-ignore
      const rowDataRateLimitAsNumber = parseInt(rowData.RateLimit, 10);
      if (isNaN(rowDataRateLimitAsNumber)) {
        this.setState({
          snackbarMessage: 'Item not saved. You must define a numeric rate limit',
          snackbarError: true,
        });
        return;
      }

      this.setState(
        {
          updating: true,
        },
        () => {
          const newDemandSourceRateLimit = {
            ID: 0,
            PublisherID: parseInt(this.props.session.publisher.ID, 10),
            DemandSourceName: rowData.DemandSource,
            RateLimit: rowDataRateLimitAsNumber,
          } as IDemandSourceRateLimit;

          createDemandSourceRateLimit(
            newDemandSourceRateLimit,
            this.props.session.publisher.ID,
          ).then(([demandSourceRateLimit, err]) => {
            if (err || !demandSourceRateLimit) {
              this.setState({
                snackbarMessage: `Error saving Rate Limit for ${
                  this.state.demandSourceList[newDemandSourceRateLimit.DemandSourceName]
                }`,
                snackbarError: true,
                updating: false,
              });
              return;
            }

            const newRow = {
              ID: demandSourceRateLimit.ID.toString(),
              Name: demandSourceRateLimit.DemandSourceName,
              DemandSource: this.state.demandSourceList[demandSourceRateLimit.DemandSourceName],
              RateLimit: demandSourceRateLimit.RateLimit,
            } as IDemandSourceRateLimitMapPairRow;
            const clonedDemandSourceRateLimitData = cloneDeep(this.state.demandSourceRateLimitData);
            clonedDemandSourceRateLimitData.push(newRow);

            // once a rate limit has been set for a given demand source,
            // we should remove that from the available demand source list so that we do not have duplicate rate limits for a given demand source
            const clonedDemandSourceDropdownList = cloneDeep(this.state.demandSourceDropdownList);
            for (let i = 0; i < clonedDemandSourceDropdownList.length; i++) {
              if (
                clonedDemandSourceDropdownList[i].props.value ===
                demandSourceRateLimit.DemandSourceName
              ) {
                clonedDemandSourceDropdownList.splice(i, 1);
              }
            }

            this.setState({
              demandSourceRateLimitData: clonedDemandSourceRateLimitData,
              demandSourceDropdownList: clonedDemandSourceDropdownList,
              snackbarMessage: `Successfully created Rate Limit for ${
                this.state.demandSourceList[demandSourceRateLimit.DemandSourceName]
              }`,
              snackbarError: false,
              updating: false,
            });
          });
        },
      );
    };

    handleChangeEditableRateLimit = (editableID: string, _: string, changedRateLimitValue: any) => {
      // the second "field" parameter should always be RateLimit for DemandSourceRateLimit rows
      if (this.state.updating) {
        return;
      }

      const changedRateLimitValueAsNumber = parseInt(changedRateLimitValue, 10);
      if (isNaN(changedRateLimitValueAsNumber)) {
        this.setState({
          snackbarMessage: 'Rate Limit not updated. Please provide a numeric rate limit',
          snackbarError: true,
        });
        return;
      }

      let updatedRateLimit = {} as IDemandSourceRateLimit;
      let rateLimitIndex = 0;
      let rateLimitFound = false;
      for (let i = 0; i < this.state.demandSourceRateLimitData.length; i++) {
        if (editableID === this.state.demandSourceRateLimitData[i].ID) {
          rateLimitIndex = i;
          rateLimitFound = true;
          updatedRateLimit = {
            // @ts-ignore
            ID: parseInt(this.state.demandSourceRateLimitData[i].ID, 10),
            PublisherID: parseInt(this.props.session.publisher.ID, 10),
            DemandSourceName: this.state.demandSourceRateLimitData[i].Name,
            RateLimit: changedRateLimitValueAsNumber,
          };
          break;
        }
      }

      if (rateLimitFound) {
        this.setState(
          {
            updating: true,
          },
          () => {
            updateDemandSourceRateLimit(
              updatedRateLimit,
              editableID,
              this.props.session.publisher.ID,
            ).then(([demandSourceRateLimit, err]) => {
              if (err || !demandSourceRateLimit) {
                this.setState({
                  snackbarMessage: `Error saving Rate Limit for ${
                    this.state.demandSourceList[updatedRateLimit.DemandSourceName]
                  }`,
                  snackbarError: true,
                  updating: false,
                });
                return;
              }

              const updatedDemandSourceRateLimitRow = {
                ID: demandSourceRateLimit.ID.toString(),
                Name: demandSourceRateLimit.DemandSourceName,
                DemandSource: this.state.demandSourceList[demandSourceRateLimit.DemandSourceName],
                RateLimit: demandSourceRateLimit.RateLimit,
              } as IDemandSourceRateLimitMapPairRow;

              const clonedDemandSourceRateLimitData = cloneDeep(
                this.state.demandSourceRateLimitData,
              );
              clonedDemandSourceRateLimitData[rateLimitIndex] = updatedDemandSourceRateLimitRow;

              this.setState({
                demandSourceRateLimitData: clonedDemandSourceRateLimitData,
                snackbarMessage: `Successfully updated rate limit for ${updatedDemandSourceRateLimitRow.DemandSource}`,
                updating: false,
              });
            });
          },
        );
      } else {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'Error Saving. Unable to reconcile demand source rate limit id',
          updating: false,
        });
      }
    };

    renderDemandSourceRateLimitTable = (): ReactElement => {
      const { demandSourceRateLimitData, loading, updating } = this.state;

      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const columns: IListTableColumn[] = [
        {
          field: 'DemandSource',
          title: 'Demand Source',
          editComponent: (props: any) =>
            this.renderEditableDemandSourceDropdownColumn(
              props,
              this.state.demandSourceDropdownList,
            ),
          renderType: 'normal',
        },
        {
          field: 'RateLimit',
          title: 'Rate Limit',
          renderType: 'editable',
          tooltip: 'Set the number of queries allowed per minute',
        },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      return (
        <Grid style={{ marginTop: 20, marginLeft: 20 }}>
          <ListTable
            title=''
            columns={columns}
            data={demandSourceRateLimitData}
            isLoading={loading}
            isUpdating={updating}
            handleDeleteItem={this.deleteDemandSourceRateLimit}
            handleChangeEditable={this.handleChangeEditableRateLimit}
            disableSnackbar={true}
            handleSetUpdating={(updating: boolean) => this.setState({ updating })}
            pageSize={10}
            actionsColumnIndex={2}
            useCustomAddButton={true}
            enableCenterActionCells={true}
            editable={{
              onRowAdd: (rowData: IDemandSourceRateLimitMapPairRow) => {
                return new Promise((resolve) => {
                  this.createNewDemandSourceRateLimit(rowData);
                  resolve(undefined);
                });
              },
            }}
          />
        </Grid>
      );
    };

    // DOOH Venue Multiplier related API
    generateDOOHVenueMultiplierTableData = () => {
      const doohVenueMultiplierMapData: IDOOHVenueMultiplierMapPairRow[] =
        this.state.DOOHVenueMultiplierMapPairs.map((pair: IDOOHVenueMultiplierMapPair) => {
          return {
            ID: pair.ID.toString(),
            Name: pair.VenueID,
            VenueID: pair.VenueID,
            VenueLatitude: pair.VenueLatitude,
            VenueLongitude: pair.VenueLongitude,
            MultiplierAmount: pair.MultiplierAmount,
            EnableEditable: false,
          };
        });
      this.setState({
        doohVenueMultiplierMapData,
      });
    };

    getDOOHVenueMultiplierMapPairsAndSetTable = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          getDOOHVenueMultiplierMapPairs(this.props.session.publisher.ID)
            .then((DOOHVenueMultiplierMapPairs: IDOOHVenueMultiplierMapPair[]) => {
              this.setState(
                {
                  loading: false,
                  DOOHVenueMultiplierMapPairs,
                },
                this.generateDOOHVenueMultiplierTableData,
              );
            })
            .catch(() => {
              this.setState({
                loading: false,
                snackbarError: true,
                snackbarMessage: 'Error loading DOOH Venue Multiplier Map',
              });
            });
        },
      );
    };

    getGlobalBlockListDataAndSetTable = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          getGlobalAppBundleBlockList(this.props.session.publisher.ID)
            .then((globalAppBundleBlockList: IGlobalAppBundleBlockList[]) => {
              const { resellers, publishers } = this.state;

              const globalAppBundleBlockListRowData = globalAppBundleBlockList.map(
                (blockListEntry) => {
                  const ExemptPublishersMenuList = {} as IMenuList;
                  const ExemptResellersMenuList = {} as IMenuList;

                  publishers.forEach((pub) => {
                    ExemptPublishersMenuList[pub.ID] = {
                      title: pub.Name,
                      selected: false,
                      ID: pub.ID,
                    };
                    blockListEntry.ExemptPublisherIDs.split(',')
                      .filter((v) => v.length)
                      .forEach((pubID) => {
                        if (pubID === pub.ID) {
                          ExemptPublishersMenuList[pub.ID].selected = true;
                        }
                      });
                  });

                  resellers.forEach((reseller) => {
                    ExemptResellersMenuList[reseller.ID] = {
                      title: reseller.Name,
                      selected: false,
                      ID: reseller.ID,
                    };
                    blockListEntry.ExemptResellerIDs.split(',')
                      .filter((v) => v.length)
                      .forEach((resellerID) => {
                        if (resellerID === reseller.ID) {
                          ExemptResellersMenuList[reseller.ID].selected = true;
                        }
                      });
                  });
                  return {
                    ID: blockListEntry.ID.toString(),
                    Name: blockListEntry.AppBundleID,
                    AppBundleID: blockListEntry.AppBundleID,
                    ExemptPublisherIDs: blockListEntry.ExemptPublisherIDs,
                    ExemptResellerIDs: blockListEntry.ExemptResellerIDs,
                    ExemptPublishersMenuList,
                    ExemptResellersMenuList,
                    EnableEditable: false,
                    shouldUpdate: false,
                  } as IGlobalAppBundleBlockListRowData;
                },
              );

              this.setState({
                loading: false,
                globalAppBundleBlockListRowData,
                globalAppBundleBlockList,
                enableAppBundleBlockListUpdate: false,
              });
            })
            .catch(() => {
              this.setState({
                loading: false,
                snackbarError: true,
                snackbarMessage: 'Error loading Global App Bundle Block List Data',
              });
            });
        },
      );
    };

    uploadGlobalAppBundleBlockList = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }

        uploadGlobalAppBundleBlockListfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          file,
        })
          .then((json: any) => {
            if (json.response && json.response.errors) {
              this.setState({
                snackbarMessage: `CSV Upload failed: ${json.response.errors}`,
                snackbarError: true,
              });
            } else {
              this.setState(
                {
                  snackbarMessage: 'CSV Upload successful',
                },
                this.getGlobalBlockListDataAndSetTable,
              );
            }
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'CSV Upload failed',
              snackbarError: true,
            });
          });
      }
      // reset the value of the event so the same file name can be uploaded
      event.target.value = '';
    };

    uploadDOOHVenueMultiplierMapPair = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }

        uploadDOOHCSVfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          file,
        })
          .then((json: any) => {
            if (json.response && json.response.errors) {
              this.setState({
                snackbarMessage: `CSV Upload failed: ${json.response.errors}`,
                snackbarError: true,
              });
            } else {
              this.setState(
                {
                  snackbarMessage: 'CSV Upload successful',
                },
                this.getDOOHVenueMultiplierMapPairsAndSetTable,
              );
            }
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'CSV Upload failed',
              snackbarError: true,
            });
          });
      }
      // reset the value of the event so the same file name can be uploaded
      event.target.value = '';
    };

    renderListToolbarUploadDownloadCSV = (
      props: any,
      genDownloadURL: string,
      uploadCSVFunc: (event: React.ChangeEvent<HTMLInputElement>) => void,
      saveHandler: (() => void) | null,
    ) => {
      const { classes } = this.props;
      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '180px' },
      };

      return (
        <Grid item>
          <Grid container>
            <Grid item style={{ marginLeft: 10, marginTop: 10 }} className={classes.toolbarRow}>
              <Grid container direction='row' justify='space-between' alignItems='center'>
                {/* Buttons */}
                <Grid item>
                  <Grid
                    container
                    direction='row'
                    justify='space-between'
                    alignItems='center'
                    spacing={4}
                  >
                    <Grid item style={{ paddingTop: 25, paddingBottom: 40 }}>
                      <Button
                        style={{ color: '#4952FF' }}
                        variant='text'
                        component='a'
                        startIcon={<GetAppIcon />}
                        target='_blank'
                        rel='noopener'
                        href={genDownloadURL}
                      >
                        Download CSV
                      </Button>
                    </Grid>
                    <Grid item style={{ paddingTop: 25, paddingBottom: 40 }}>
                      <label>
                        <input
                          type='file'
                          style={{ display: 'none' }}
                          accept='.csv'
                          onChange={uploadCSVFunc}
                        />
                        <Button
                          style={{ color: '#4952FF' }}
                          variant='text'
                          component='span'
                          startIcon={<PublishIcon />}
                        >
                          Upload CSV
                        </Button>
                      </label>
                    </Grid>
                    {
                      // only for Global BlockList Tab for now
                      !!saveHandler && (
                        <Grid item style={{ paddingTop: 25, paddingBottom: 40 }}>
                          <Button
                            disabled={!this.state.enableAppBundleBlockListUpdate}
                            onClick={saveHandler}
                            style={{
                              color: this.state.enableAppBundleBlockListUpdate
                                ? '#4952FF'
                                : '#B2BEB5',
                            }}
                            variant='text'
                            component='span'
                            startIcon={<SaveIcon />}
                          >
                            Update Exemptions
                          </Button>
                        </Grid>
                      )
                    }
                  </Grid>
                </Grid>
                {/* search bar - grid with class "MTableToolbarWrapper" is required for auto focusing search bar on load */}
                <Grid item className='MTableToolbarWrapper'>
                  <MTableToolbar {...copyProps} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDOOHVenueMultiplierMapPairs = (): ReactElement => {
      const { doohVenueMultiplierMapData, loading, updating } = this.state;

      const columns: IListTableColumn[] = [
        { field: 'VenueID', title: 'Venue', renderType: 'normal' },
        { field: 'MultiplierAmount', title: 'Multiplier', renderType: 'normal' },
        { field: 'VenueLatitude', title: 'Venue Latitude', renderType: 'normal' },
        { field: 'VenueLongitude', title: 'Venue Longitude', renderType: 'normal' },
      ];

      return (
        <Grid style={{ marginTop: 20, marginLeft: 20 }}>
          <ListTable
            title=''
            columns={columns}
            data={doohVenueMultiplierMapData}
            isLoading={loading}
            isUpdating={updating}
            renderCustomToolbar={(props: any) =>
              this.renderListToolbarUploadDownloadCSV(
                props,
                genDownloadDOOHCSVURL(this.props.session.publisher.ID),
                this.uploadDOOHVenueMultiplierMapPair,
                null,
              )
            }
            pageSize={10}
          />
        </Grid>
      );
    };

    renderMenuList = (
      menuList: IGlobalAppBundleBlockListRowData['ExemptPublishersMenuList'],
      rowID: string,
      menuTitle: string,
    ) => {
      return (
        <MaterialSearchDropdownMulti
          width='200px'
          fontSize='16px'
          maxHeight={325}
          menuTitle={menuTitle}
          list={menuList}
          enableMultiSelect
          overrideDefaultSortMode={SortByMode.IdAsc}
          enableFixed
          showPrefixID
          onChange={(updatedList) => {
            const globalAppBundleBlockListRowData: IGlobalAppBundleBlockListRowData[] =
              this.state.globalAppBundleBlockListRowData.map(
                (blockListEntry: IGlobalAppBundleBlockListRowData) => {
                  if (blockListEntry.ID === rowID && menuTitle === 'Exempt Publishers') {
                    blockListEntry.ExemptPublishersMenuList = updatedList;
                    // transform menuList to comma separated publisher IDs
                    blockListEntry.ExemptPublisherIDs = Object.values(updatedList)
                      .map((item) => (item.selected ? item.ID : ''))
                      .filter((v) => v.length)
                      .join(',');
                    blockListEntry.shouldUpdate = true;
                  }
                  if (blockListEntry.ID === rowID && menuTitle === 'Exempt Resellers') {
                    blockListEntry.ExemptResellersMenuList = updatedList;
                    // transform menuList to comma separated reseller IDs
                    blockListEntry.ExemptResellerIDs = Object.values(updatedList)
                      .map((item) => (item.selected ? item.ID : ''))
                      .filter((v) => v.length)
                      .join(',');
                    blockListEntry.shouldUpdate = true;
                  }
                  return blockListEntry;
                },
              );
            this.setState({
              globalAppBundleBlockListRowData,
              enableAppBundleBlockListUpdate: true,
            });
          }}
        />
      );
    };

    handleGlobalAppBundleBlockListSave = () => {
      const { globalAppBundleBlockListRowData, globalAppBundleBlockList } = this.state;

      const globalAppBundleBlockListUpdates = [] as IGlobalAppBundleBlockList[];
      globalAppBundleBlockList.forEach((origEntry) => {
        globalAppBundleBlockListRowData.forEach((updatedEntry) => {
          // only update if data changed
          if (origEntry.ID.toString() === updatedEntry.ID && updatedEntry.shouldUpdate) {
            const entryCloneToUpdate = cloneDeep(origEntry);
            entryCloneToUpdate.ExemptPublisherIDs = updatedEntry.ExemptPublisherIDs;
            entryCloneToUpdate.ExemptResellerIDs = updatedEntry.ExemptResellerIDs;
            globalAppBundleBlockListUpdates.push(entryCloneToUpdate);
          }
        });
      });

      updateGlobalAppBundleBlockList(
        globalAppBundleBlockListUpdates,
        this.props.session.publisher.ID,
      )
        .then(this.getGlobalBlockListDataAndSetTable)
        .catch((err) => {
          this.setState({
            snackbarMessage: `Updating App Bundle Block List Failed: ${err}`,
            snackbarError: true,
          });
        });
    };

    renderGlobalAppBundleBlockList = (): ReactElement => {
      const { globalAppBundleBlockListRowData, loading, updating } = this.state;

      const columns: IListTableColumn[] = [
        { field: 'AppBundleID', title: 'App Bundle ID', renderType: 'normal' },
        {
          field: 'ExemptPublisherIDs',
          title: 'Exempt Publishers',
          render: (rowData: IGlobalAppBundleBlockListRowData) =>
            this.renderMenuList(rowData.ExemptPublishersMenuList, rowData.ID, 'Exempt Publishers'),
          searchable: true,
          width: '25%',
        },
        {
          field: 'ExemptResellerIDs',
          title: 'Exempt Resellers',
          render: (rowData: IGlobalAppBundleBlockListRowData) =>
            this.renderMenuList(rowData.ExemptResellersMenuList, rowData.ID, 'Exempt Resellers'),
          searchable: true,
          width: '25%',
        },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      return (
        <Grid style={{ marginTop: 20, marginLeft: 20 }}>
          <ListTable
            title=''
            columns={columns}
            data={globalAppBundleBlockListRowData}
            handleDeleteItem={this.deleteGlobalAppBundleEntry}
            isLoading={loading}
            isUpdating={updating}
            handleSetUpdating={(updating: boolean) => this.setState({ updating })}
            renderCustomToolbar={(props: any) =>
              this.renderListToolbarUploadDownloadCSV(
                props,
                genDownloadGlobalAppBundleBlockListURL(),
                this.uploadGlobalAppBundleBlockList,
                this.handleGlobalAppBundleBlockListSave,
              )
            }
            pageSize={25}
            useCustomAddButton={true}
            actionsColumnIndex={3}
            enableCenterActionCells={true}
            editable={{
              onRowAdd: (rowData: IGlobalAppBundleBlockListRowData) => {
                return new Promise((resolve) => {
                  if (!rowData.AppBundleID) {
                    rowData.AppBundleID = '';
                  }
                  if (!rowData.ExemptPublisherIDs) {
                    rowData.ExemptPublisherIDs = '';
                  }
                  if (!rowData.ExemptResellerIDs) {
                    rowData.ExemptResellerIDs = '';
                  }
                  this.createGlobalAppBundleBlockListEntry(
                    rowData.AppBundleID,
                    rowData.ExemptPublisherIDs,
                    rowData.ExemptResellerIDs,
                  );
                  resolve(undefined);
                });
              },
            }}
          />
        </Grid>
      );
    };

    createGlobalAppBundleBlockListEntry(
      AppBundleID: string,
      ExemptPublisherIDs: string,
      ExemptResellerIDs: string,
    ) {
      if (AppBundleID === '') {
        this.setState({
          snackbarMessage: 'AppBundleID is required',
          snackbarError: true,
        });
        return;
      }
      // check to make sure the new App Bundle ID is unique
      for (const entry of this.state.globalAppBundleBlockList) {
        if (AppBundleID === entry.AppBundleID) {
          this.setState({
            snackbarMessage: 'AppBundleID must be unique',
            snackbarError: true,
          });
          return;
        }
      }

      this.setState(
        {
          updating: true,
        },
        () => {
          createGlobalAppBundleBlockList(
            {
              ID: 0,
              AppBundleID,
              ExemptPublisherIDs,
              ExemptResellerIDs,
            },
            this.props.session.publisher.ID,
          )
            .then(() => {
              this.setState(
                {
                  updating: false,
                  snackbarMessage: 'Successfully created global app bundle blocklist entry',
                },
                this.getGlobalBlockListDataAndSetTable,
              );
            })
            .catch((resp) => {
              this.setState({
                updating: false,
                snackbarMessage: `Failed to create global app bundle blocklist entry: ${resp}`,
                snackbarError: true,
              });
            });
        },
      );
    }

    // EPG Channel related API
    generateEPGChannelTableData = () => {
      const epgChannelMapData: IEPGChannelMapPairRow[] = this.state.EPGChannelMapPairs.map(
        (pair: IEPGChannelMapPair) => {
          return {
            ID: pair.ID.toString(),
            Name: pair.EPGChannelID,
            EPGChannelID: pair.EPGChannelID,
            PublicaChannelID: pair.PublicaChannelID,
            PublicaChannelName: pair.PublicaChannelName,
            ContentChannel: pair.ContentChannel,
            EnableEditable: false,
          };
        },
      );
      this.setState({
        epgChannelMapData,
      });
    };

    getEPGChannelMapPairsAndSetTable = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          getEPGChannelMapPairs(this.props.session.publisher.ID)
            .then((EPGChannelMapPairs: IEPGChannelMapPair[]) => {
              this.setState(
                {
                  loading: false,
                  EPGChannelMapPairs,
                },
                this.generateEPGChannelTableData,
              );
            })
            .catch(() => {
              this.setState({
                loading: false,
                snackbarError: true,
                snackbarMessage: 'Error loading DOOH Venue Multiplier Map',
              });
            });
        },
      );
    };

    uploadEPGChannelMapPair = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }

        uploadEPGChannelCSVfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          file,
        })
          .then((json: any) => {
            if (json.response && json.response.errors) {
              this.setState({
                snackbarMessage: `CSV Upload failed: ${json.response.errors}`,
                snackbarError: true,
              });
            } else {
              this.setState(
                {
                  snackbarMessage: 'CSV Upload successful',
                },
                this.getEPGChannelMapPairsAndSetTable,
              );
            }
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'CSV Upload failed',
              snackbarError: true,
            });
          });
      }
      // reset the value of the event so the same file name can be uploaded
      event.target.value = '';
    };

    renderEPGChannelMapPairs = (): ReactElement => {
      const { epgChannelMapData, loading, updating } = this.state;

      const columns: IListTableColumn[] = [
        { field: 'EPGChannelID', title: 'EPG Channel ID', renderType: 'normal' },
        { field: 'PublicaChannelID', title: 'Publica Channel ID', renderType: 'normal' },
        { field: 'PublicaChannelName', title: 'Publica Channel Name', renderType: 'normal' },
        { field: 'ContentChannel', title: 'Content Channel', renderType: 'normal' },
      ];

      return (
        <Grid style={{ marginTop: 20, marginLeft: 20 }}>
          <ListTable
            title=''
            columns={columns}
            data={epgChannelMapData}
            isLoading={loading}
            isUpdating={updating}
            renderCustomToolbar={(props: any) =>
              this.renderListToolbarUploadDownloadCSV(
                props,
                genDownloadEPGChannelCSVURL(this.props.session.publisher.ID),
                this.uploadEPGChannelMapPair,
                null,
              )
            }
            pageSize={10}
          />
        </Grid>
      );
    };

    generateLiverampCustomIDListTableData = () => {
      const liverampCustomIDMapData: ILiverampCustomIDMapPairRow[] =
        this.state.LiverampCustomIDMapPairs.map((pair: ILiverampCustomIDMapPair) => {
          return {
            Name: 'Publisher-Liveramp Pair: ' + pair.PublisherID,
            ID: pair.ID.toString(),
            PublisherID: pair.PublisherID,
            LiverampCustomID: pair.LiverampCustomID,
            EnableEditable: false,
          };
        });
      this.setState({
        liverampCustomIDMapData,
      });
    };

    // liveramp-custom-id related api
    getLiverampCustomIDMapPairsAndSetTable = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          getLiverampCustomIDMapPairs()
            .then((LiverampCustomIDMapPairs: ILiverampCustomIDMapPair[]) => {
              this.setState(
                {
                  loading: false,
                  LiverampCustomIDMapPairs,
                },
                this.generateLiverampCustomIDListTableData,
              );
            })
            .catch(() => {
              this.setState({
                loading: false,
                snackbarError: true,
                snackbarMessage: 'Error loading Liveramp Custom ID Map',
              });
            });
        },
      );
    };

    createLiverampCustomIDMapPair = (PublisherID: number, LiverampCustomID: number) => {
      // check to make sure the new key is unique
      for (const pair of this.state.LiverampCustomIDMapPairs) {
        if (PublisherID === pair.PublisherID) {
          this.setState({
            snackbarMessage: 'PublisherID must be unique',
            snackbarError: true,
          });
          return;
        }
      }

      this.setState(
        {
          updating: true,
        },
        () => {
          createLiverampCustomIDMapPair({
            ID: 0,
            PublisherID,
            LiverampCustomID,
          })
            .then(() => {
              this.setState(
                {
                  updating: false,
                  snackbarMessage: 'Successfully created PublisherID-LiverampCustomID map pair',
                },
                this.getLiverampCustomIDMapPairsAndSetTable,
              );
            })
            .catch((resp) => {
              this.setState({
                updating: false,
                snackbarMessage: `Failed to create PublisherID-LiverampCustomID map pair: ${resp}`,
                snackbarError: true,
              });
            });
        },
      );
    };

    deleteLiverampCustomIDMapPair = (id: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteLiverampCustomIDMapPair(parseInt(id, 10))
            .then(() => {
              this.setState(
                {
                  snackbarMessage: 'PublisherID-LiverampCustomID pair deleted successfully.',
                  updating: false,
                },
                this.getLiverampCustomIDMapPairsAndSetTable,
              );
            })
            .catch(() => {
              this.setState({
                snackbarMessage: 'PublisherID-LiverampCustomID pair deletion failed',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return Promise.resolve(undefined);
    };

    updateLiverampCustomIDMapPair = (editableID: string, field: string, changedValue: any) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          const liverampData = cloneDeep(this.state.liverampCustomIDMapData);
          let unchangedValue;
          for (const datum of liverampData) {
            if (datum.ID === editableID) {
              unchangedValue = field === 'PublisherID' ? datum.LiverampCustomID : datum.PublisherID;
              break;
            }
          }
          updateLiverampCustomIDMapPair({
            ID: parseInt(editableID, 10),
            PublisherID: field === 'PublisherID' ? changedValue : unchangedValue,
            LiverampCustomID: field === 'LiverampCustomID' ? changedValue : unchangedValue,
          })
            .then(() => {
              this.getLiverampCustomIDMapPairsAndSetTable();
              this.setState({
                snackbarMessage: 'PublisherID-LiverampCustomID pair successfully updated.',
                updating: false,
              });
            })
            .catch((resp) => {
              this.setState({
                snackbarMessage: `API PublisherID-LiverampCustomID pair failed to update: ${resp}`,
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
    };

    // changes which item is being edited and sets the temp ID and name
    // the temp name makes it so we can change back to the old name if the user cancels
    // only one item can be edited at a time for this reason
    // the temp name is switched before rendering
    handleChangeEditableLiverampCustomIDMap = (
      editableID: string,
      field: string,
      changedValue: any,
    ) => {
      if (this.state.updating) {
        return;
      }
      // validate that the name and url are not blank before updating
      if (field === 'PublisherID' && changedValue <= 0) {
        this.setState({
          snackbarMessage: 'PublisherID must be larger than 0',
          snackbarError: true,
        });
        return;
      } else if (field === 'LiverampCustomID' && changedValue === 0) {
        this.setState({
          snackbarMessage: 'LiverampCustomID must be larger than 0',
          snackbarError: true,
        });
        return;
      }
      this.updateLiverampCustomIDMapPair(editableID, field, changedValue);
    };

    renderLiverampCustomIDMapPairs = (): ReactElement => {
      const {
        liverampCustomIDMapData,
        loading,
        updating,
        editableID,
        editablePublisherID,
        editableLiverampCustomID,
      } = this.state;

      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const columns: IListTableColumn[] = [
        {
          field: 'PublisherID',
          title: 'Publisher ID',
          renderType: 'editable',
          initialEditValue: 'Enter Publisher ID',
        },
        {
          field: 'LiverampCustomID',
          title: 'LiveRamp Custom ID',
          renderType: 'editable',
          initialEditValue: 'Enter LiveRamp Custom ID',
        },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      // loop over data to switch the name of the edited row to the temp name
      const clonedData = cloneDeep(liverampCustomIDMapData);
      for (const datum of clonedData) {
        if (datum.ID === editableID) {
          datum.PublisherID = editablePublisherID;
          datum.LiverampCustomID = editableLiverampCustomID;
          break;
        }
      }

      return (
        <Grid style={{ marginTop: 20, marginLeft: 20 }}>
          <ListTable
            title=''
            columns={columns}
            data={clonedData}
            isLoading={loading}
            isUpdating={updating}
            handleChangeEditable={this.handleChangeEditableLiverampCustomIDMap}
            handleDeleteItem={this.deleteLiverampCustomIDMapPair}
            handleSetUpdating={(updating: boolean) => this.setState({ updating })}
            pageSize={10}
            actionsColumnIndex={2}
            useCustomAddButton={true}
            enableCenterActionCells={true}
            editable={{
              onRowAdd: (rowData: ILiverampCustomIDMapPairRow) => {
                return new Promise((resolve) => {
                  this.createLiverampCustomIDMapPair(rowData.PublisherID, rowData.LiverampCustomID);
                  resolve(undefined);
                });
              },
            }}
          />
        </Grid>
      );
    };

    renderTabs(): ReactElement {
      const isSuper = hasRoleSuperUser(this.props.user.user);
      const isPublica = this.props.session.publisher.ID === '1';
      const enableAPSPriceMap = ['381', '401', '518', '715', '1083'].includes(
        this.props.session.publisher.ID,
      );
      const tabs: ITab[] = [
        {
          label: 'DOOH Venue Multiplier Map',
          render: this.renderDOOHVenueMultiplierMapPairs,
        },
      ];

      if (isSuper && this.props.session.publisher.ContextualSegmentEnabled === EBool.EBool_TRUE) {
        tabs.push({
          label: 'EPG Channel Map *',
          render: this.renderEPGChannelMapPairs,
        });
      }

      if (isPublica || enableAPSPriceMap) {
        tabs.push({
          label: 'APS Price Map',
          render: this.renderAPSPriceMapPairs,
        });
      }

      if (isPublica && isSuper) {
        tabs.push(
          {
            label: 'LiveRamp Custom ID Map *',
            render: this.renderLiverampCustomIDMapPairs,
          },
          {
            label: 'Global App Bundle Block List *',
            render: this.renderGlobalAppBundleBlockList,
          },
        );
      }

      tabs.push({
        label: 'Demand Source Rate Limit Map',
        render: this.renderDemandSourceRateLimitTable,
      });

      return <SettingsTabs tabs={tabs} marginTop={0} maxTabWidth='1200px' />;
    }

    render() {
      const { snackbarMessage, snackbarError } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderTabs()}
            {/* snackbar error msgs */}
            <SnackbarView
              autoHideDuration={30000}
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
